import axios, { AxiosError, AxiosResponse } from 'axios';
import { toPath } from 'lodash';
import { notification } from 'antd';
import logger from '../utils/logger';

const { isCancel } = axios;

const ERROR_MSG = {
  400: 'Validation Error',
  404: 'Page not found',
  403: 'Incorrect entry',
  418: 'Nothing to change',
  401: 'Unauthorised',
  500: 'Server Error',
};

const UNKOWN = 'Unknown Error (API)';

function getErrorMessage(error: any): string {
  if (!error) return UNKOWN;
  if (!error.response) return error.message || UNKOWN;

  const {
    response,
  }: {
    response: { data?: { message: string }; message?: string; status: keyof typeof ERROR_MSG };
  } = error;
  if (response.data) {
    if (response.data.message) {
      return response.data.message;
    }
  }
  if (response.message) {
    return response.message;
  }
  return ERROR_MSG[response.status];
}

type HandlerOptions = { notification?: string | boolean };

function basicErrorHandler(
  error: AxiosError,
  { notification: notificationMsg = true }: HandlerOptions = {},
) {
  // Request manually cancelled using CancelToken
  if (isCancel(error)) return;
  const description = getErrorMessage(error);
  if (notificationMsg) {
    notification.error({
      message: typeof notificationMsg === 'string' ? notificationMsg : 'Failure',
      description,
    });
  }
}

function basicSuccessHandler(
  res: AxiosResponse,
  { notification: notificationMsg = false }: HandlerOptions = {},
) {
  if (!notificationMsg) return res;
  const description = res.data.message;
  notification.success({
    message: typeof notificationMsg === 'string' ? notificationMsg : 'Success',
    description,
  });
  return res;
}

function convertToIndex(path: string) {
  const index = Number(path);
  if (Number.isNaN(index)) return path;
  return index;
}

function getFormErrorsFromError(error: AxiosError<any>) {
  const errors = error?.response?.data?.errors;
  if (!errors) {
    logger.error({ err: error }, 'NON_FORM_ERROR');
    return [];
  }
  return Object.keys(errors).map((path) => ({
    errors: [errors[path]],
    name: toPath(path).map(convertToIndex),
  }));
}

function setFormErrors(error: AxiosError, formRef: any) {
  if (isCancel(error)) return;
  const fieldData = getFormErrorsFromError(error);

  if (fieldData.length) {
    formRef.current?.setFields(fieldData);
    formRef.current?.scrollToField(fieldData[0].name);
    // @ts-ignore
    error.fieldData = fieldData;
  }
}

function wrapInIsCancel(errHandler: (err: AxiosError) => any) {
  return (err: AxiosError) => {
    if (isCancel(err)) return {};
    return errHandler(err);
  };
}

export {
  isCancel,
  wrapInIsCancel,
  getFormErrorsFromError,
  setFormErrors,
  getErrorMessage,
  basicErrorHandler,
  basicSuccessHandler,
};
